import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { statisticsManager, PlayerStatistics, DailyStats, CategoryRecord } from '../utils/statisticsManager';
import { TrendLineChart, CategoryAvgBars } from './StatCharts';

interface StatisticsModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const Overlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 900px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 30px;
  border-radius: 16px 16px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Content = styled.div`
  padding: 30px;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid #eee;
  margin-bottom: 30px;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  color: ${props => props.$active ? '#667eea' : '#666'};
  border-bottom: 3px solid ${props => props.$active ? '#667eea' : 'transparent'};
  transition: all 0.3s ease;
  
  &:hover {
    color: #667eea;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  border: 1px solid #dee2e6;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: #667eea;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ChartContainer = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
`;

// CHANGE (2026-08-15): SimpleChart/ChartBar ersatta av StatCharts (TrendLineChart).

const CategoryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
`;

const CategoryItem = styled.div`
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CategoryName = styled.div`
  font-weight: bold;
  color: #333;
`;

const CategoryStats = styled.div`
  text-align: right;
`;

const CategoryBest = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #667eea;
`;

const CategoryAverage = styled.div`
  font-size: 12px;
  color: #666;
`;

const formatTime = (milliseconds: number): string => {
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('sv-SE', {
    month: 'short',
    day: 'numeric'
  });
};

const StatisticsModal: React.FC<StatisticsModalProps> = ({ isVisible, onClose }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'records'>('overview');
  const [stats, setStats] = useState<PlayerStatistics | null>(null);
  const [performanceData, setPerformanceData] = useState<DailyStats[]>([]);
  const [categoryRecords, setCategoryRecords] = useState<CategoryRecord[]>([]);

  useEffect(() => {
    if (isVisible) {
      const currentStats = statisticsManager.getStatistics();
      setStats(currentStats);
      setPerformanceData(statisticsManager.getPerformanceTrend(30));
      setCategoryRecords(statisticsManager.getBestCategoryScores());
    }
  }, [isVisible]);

  if (!isVisible || !stats) return null;

  const renderOverview = () => (
    <>
      <StatsGrid>
        <StatCard>
          <StatValue>{stats.totalGames}</StatValue>
          <StatLabel>Spelade spel</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{Math.round(stats.averageScore)}</StatValue>
          <StatLabel>Genomsnitt</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.bestScore}</StatValue>
          <StatLabel>Bästa poäng</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.currentStreak}</StatValue>
          <StatLabel>Nuvarande streak</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.longestStreak}</StatValue>
          <StatLabel>Längsta streak</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatTime(stats.totalPlayTime)}</StatValue>
          <StatLabel>Total speltid</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.yatzyCount}</StatValue>
          <StatLabel>Yatzy antal</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.perfectGames}</StatValue>
          <StatLabel>Perfekta spel</StatLabel>
        </StatCard>
      </StatsGrid>
    </>
  );

  const renderPerformance = () => {
    // CHANGE (2026-08-15): Riktigt linjediagram (StatCharts) istället för den
    // gamla stapelraden med siffror på varje stapel.
    return (
      <>
        <ChartContainer>
          <ChartTitle>Poängtrend – senaste 30 dagarna</ChartTitle>
          <TrendLineChart days={performanceData} />
        </ChartContainer>

        <StatsGrid>
          <StatCard>
            <StatValue>{Math.round(stats.averageRollsPerCategory * 10) / 10}</StatValue>
            <StatLabel>Snitt kast/kategori</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatTime(stats.fastestGame)}</StatValue>
            <StatLabel>Snabbaste spel</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.bonusCount}</StatValue>
            <StatLabel>Bonusar erhållna</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{Math.round((stats.perfectGames / stats.totalGames) * 100) || 0}%</StatValue>
            <StatLabel>Perfekta spel %</StatLabel>
          </StatCard>
        </StatsGrid>
      </>
    );
  };

  const renderRecords = () => (
    <>
      <ChartContainer>
        <ChartTitle>Snitt per kategori (andel av max)</ChartTitle>
        <CategoryAvgBars records={categoryRecords} getName={getCategoryDisplayName} />
      </ChartContainer>
      <ChartTitle>Bästa poäng per kategori</ChartTitle>
      <CategoryList>
        {categoryRecords.map(record => (
          <CategoryItem key={record.category}>
            <div>
              <CategoryName>{getCategoryDisplayName(record.category)}</CategoryName>
              <CategoryAverage>
                Snitt: {record.averageScore} ({record.timesUsed} gånger)
              </CategoryAverage>
            </div>
            <CategoryStats>
              <CategoryBest>{record.bestScore}</CategoryBest>
              <CategoryAverage>{formatDate(record.date)}</CategoryAverage>
            </CategoryStats>
          </CategoryItem>
        ))}
      </CategoryList>
    </>
  );

  const getCategoryDisplayName = (key: string): string => {
    const names: Record<string, string> = {
      ones: 'Ettor', twos: 'Tvåor', threes: 'Treor', fours: 'Fyror', fives: 'Femmor', sixes: 'Sexor',
      onePair: 'Ett par', twoPairs: 'Två par', threeOfAKind: 'Triss', fourOfAKind: 'Fyrtal',
      smallStraight: 'Liten stege', largeStraight: 'Stor stege', fullHouse: 'Kåk',
      chance: 'Chans', yatzy: 'Yatzy'
    };
    return names[key] || key;
  };

  return (
    <Overlay $isVisible={isVisible} onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <Header>
          <Title>📊 Spelstatistik</Title>
          <CloseButton onClick={onClose}>Stäng</CloseButton>
        </Header>
        
        <Content>
          <TabContainer>
            <Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
              Översikt
            </Tab>
            <Tab $active={activeTab === 'performance'} onClick={() => setActiveTab('performance')}>
              Prestanda
            </Tab>
            <Tab $active={activeTab === 'records'} onClick={() => setActiveTab('records')}>
              Rekord
            </Tab>
          </TabContainer>

          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'performance' && renderPerformance()}
          {activeTab === 'records' && renderRecords()}
        </Content>
      </ModalContainer>
    </Overlay>
  );
};

export default StatisticsModal;
